/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "@app/vue/pages/Inventory/subpages/Stock/types";
import { lineCost, toBaseQty, type EditableLine } from "./recipeCost";
import { RecipeLineRow } from "./RecipeLineRow";

type Props = {
  entityType: "product" | "extra";
  entityId: number;
  entityName: string;
  /** Menu price incl. VAT, for the cost-to-price ratio. */
  price: number | null;
  onClose: () => void;
};

// Edits which stock a product (or an extra chosen with one) uses, with the
// cost per portion at the latest prices paid.
// See docs/plans/inventory_and_costing/03_data_model.md (#6).
export const RecipeEditorModal = defineComponent<Props>({
  name: "RecipeEditorModal",
  props: ["entityType", "entityId", "entityName", "price", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const saving = ref(false);
    const stockItems = ref<StockItem[]>([]);
    const lines = ref<EditableLine[]>([]);
    const yieldPortions = ref(1);

    const formatMoney = (value: number) => value.toFixed(value < 1 ? 3 : 2);

    onMounted(async () => {
      try {
        const [items, recipe] = await Promise.all([
          trpc.stock.list.query(),
          trpc.recipes.get.query({
            entity_type: props.entityType,
            entity_id: props.entityId,
          }),
        ]);
        stockItems.value = (items as StockItem[]).filter((s) => s.active);
        if (recipe) {
          yieldPortions.value = recipe.yield_portions;
          lines.value = recipe.lines.map((l) => ({
            stock_item_id: l.stock_item_id,
            net_qty: l.net_qty,
            unit: l.unit,
            loss_pct: l.loss_pct,
          }));
        }
      } catch {
        toast.showToast(t("inventory.recipe.load_failed"), { type: "error" });
        props.onClose();
      } finally {
        loading.value = false;
      }
    });

    const itemFor = (line: EditableLine) =>
      stockItems.value.find((s) => s.id === line.stock_item_id);

    const totalCost = computed(() =>
      lines.value.reduce(
        (sum, line) => sum + (lineCost(line, itemFor(line), yieldPortions.value).cost ?? 0),
        0,
      ),
    );

    const invalid = computed(
      () =>
        yieldPortions.value <= 0 ||
        lines.value.some((line) => {
          const item = itemFor(line);
          return !item || !(line.net_qty > 0) || toBaseQty(item, 1, line.unit) == null;
        }),
    );

    const save = async () => {
      saving.value = true;
      try {
        await trpc.recipes.upsert.mutate({
          entity_type: props.entityType,
          entity_id: props.entityId,
          yield_portions: yieldPortions.value,
          lines: lines.value.map((l) => ({
            stock_item_id: l.stock_item_id!,
            net_qty: l.net_qty,
            unit: l.unit,
            loss_pct: l.loss_pct || 0,
          })),
        });
        toast.showToast(t("inventory.recipe.saved"), { type: "success" });
        props.onClose();
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ?? t("inventory.recipe.save_failed"),
          { type: "error" },
        );
      } finally {
        saving.value = false;
      }
    };

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        );
      }
      if (stockItems.value.length === 0) {
        return (
          <div class="p-6 text-sm text-base-content/70">{t("inventory.recipe.no_stock_items")}</div>
        );
      }

      const ratio =
        props.price && props.price > 0 ? (totalCost.value / props.price) * 100 : null;

      return (
        <div class="space-y-4 p-6">
          <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>{t("inventory.recipe.col_item")}</th>
                <th>{t("inventory.recipe.col_qty")}</th>
                <th>{t("inventory.recipe.col_unit")}</th>
                <th>{t("inventory.recipe.col_loss")}</th>
                <th class="text-right">{t("inventory.recipe.col_cost")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.value.map((line, index) => (
                <RecipeLineRow
                  key={index}
                  line={line}
                  stockItems={stockItems.value}
                  yieldPortions={yieldPortions.value}
                  formatMoney={formatMoney}
                  onChange={(next: EditableLine) =>
                    (lines.value = lines.value.map((l, i) => (i === index ? next : l)))
                  }
                  onRemove={() => (lines.value = lines.value.filter((_, i) => i !== index))}
                />
              ))}
            </tbody>
          </table>
          </div>

          <button
            class="btn btn-ghost btn-sm"
            onClick={() =>
              (lines.value = [
                ...lines.value,
                { stock_item_id: null, net_qty: 0, unit: "", loss_pct: 0 },
              ])
            }
          >
            <span class="material-symbols-outlined text-base">add</span>
            {t("inventory.recipe.add_line")}
          </button>

          <div class="flex flex-wrap items-end justify-between gap-4 border-t border-base-300 pt-4">
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("inventory.recipe.yield")}</span>
              <input
                type="number"
                min="1"
                step="any"
                class="input input-bordered input-sm w-24"
                value={yieldPortions.value}
                onInput={(e: Event) =>
                  (yieldPortions.value = Number((e.target as HTMLInputElement).value))
                }
              />
              <span class="mt-1 block text-xs text-base-content/50">
                {t("inventory.recipe.yield_help")}
              </span>
            </label>
            <div class="text-right">
              <div class="text-sm text-base-content/60">{t("inventory.recipe.cost_per_portion")}</div>
              <div class="text-2xl font-semibold">{formatMoney(totalCost.value)}</div>
              {ratio != null && (
                <div class="text-xs text-base-content/60">
                  {t("inventory.recipe.cost_ratio").replace("{percent}", ratio.toFixed(1))}
                </div>
              )}
            </div>
          </div>
          <p class="text-xs text-base-content/50">{t("inventory.recipe.cost_help")}</p>
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={t("inventory.recipe.title").replace("{name}", props.entityName)}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose()}>
                {t("general.words.cancel")}
              </button>
              <button
                class="btn btn-primary"
                disabled={loading.value || saving.value || invalid.value}
                onClick={save}
              >
                {t("general.words.save")}
              </button>
            </div>
          </div>
        )}
      >
        {renderBody()}
      </LemmonModal>
    );
  },
});
